import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRightIcon, ShieldCheckIcon, StarIcon, PlayIcon } from "lucide-react";
import { Navbar } from "./Navbar";
import { PillButton } from "./PillButton";
import { images } from "../data/images";
const ease = [0.23, 1, 0.32, 1] as const;
const tags = ["Corporate", "Property", "Litigation"];
const avatars = ["JR", "SL", "AK"];
export function Hero() {
  const reduce = useReducedMotion();
  const enter = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      delay,
      ease
    }
  };
  return <section className="w-full bg-white p-2 sm:p-3" data-id="element-107" data-mp="ncgjad4b0lso">
      <div className="relative flex min-h-[100svh] flex-col overflow-hidden rounded-2xl bg-ink sm:rounded-[20px] md:min-h-[860px] xl:h-[calc(100vh-24px)] xl:min-h-[820px] xl:max-h-[940px]" data-id="element-108" data-mp="nbyhl0yvhfdd">
        <img src={images.hero} alt="A solicitor reviewing a document beside a tall window overlooking the City of London" className="absolute inset-0 h-full w-full object-cover object-[72%_center]" data-id="element-109" data-mp="380vz9yqbyvdo" />
        <div aria-hidden="true" className="absolute inset-0 bg-ink/60 md:bg-ink/50 xl:bg-ink/40" data-id="element-110" data-mp="39acysrtohsul" />

        <Navbar data-id="element-111" data-mp="3e9fq79gqk0k3" />

        <div className="relative z-10 mx-auto mt-auto grid w-full max-w-[1392px] grid-cols-12 items-end gap-x-6 gap-y-10 px-5 pb-8 pt-28 sm:px-8 sm:pb-10 md:px-12 md:pb-14 lg:px-14" data-id="element-112" data-mp="3e8wjkowjcyld">
          <div className="col-span-12 xl:col-span-8" data-id="element-113" data-mp="358rfooqjwiby">
            <motion.div {...enter(0)} className="flex items-center gap-3" data-id="element-114" data-mp="35aas2493698g">
              <span className="flex items-center gap-0.5" aria-hidden="true" data-id="element-115" data-mp="2qkwk90l5rzv6">
                {Array.from({
                length: 5
              }).map((_, i) => <StarIcon key={i} className="h-3.5 w-3.5 fill-accent text-accent" strokeWidth={0} data-id="element-116" data-mp="2vgu0l6wf967y" data-mpi={i} />)}
              </span>
              <span className="text-[14px] font-semibold text-white" data-id="element-117" data-mp="2lla4okhuy7j7">4.9</span>
              <span className="text-[13px] text-white/60" data-id="element-118" data-mp="s21j2e1koi11">Rated by 120+ clients</span>
            </motion.div>

            <motion.ul {...enter(0.03)} className="mt-5 flex flex-wrap gap-2" data-id="element-119" data-mp="nbl3ooe6ymc1">
              {tags.map(tag => <li key={tag} className="rounded-full border border-white/25 px-3 py-1 text-[12px] font-medium text-white/80" data-id="element-120" data-mp="ibgnkzh73tmc">
                  {tag}
                </li>)}
            </motion.ul>

            <motion.h1 {...enter(0.06)} className="mt-6 text-[46px] font-semibold leading-[0.95] tracking-[-0.045em] text-white sm:text-[64px] md:text-[80px] xl:text-[104px] xl:leading-[0.92]" data-id="element-121" data-mp="161t2wverssmy">
              Legal clarity,
              <br data-id="element-122" data-mp="x0l2o7sp1hq8" />
              <span className="text-white/55" data-id="element-123" data-mp="x37bearbm3a1">
                practical <br className="hidden xl:block" data-id="element-124" data-mp="3lu02l0av52z7" />
                outcomes.
              </span>
            </motion.h1>

            <motion.p {...enter(0.1)} className="mt-6 max-w-[480px] text-[16px] leading-[1.6] text-white/75 sm:mt-7 sm:text-[17px]" data-id="element-125" data-mp="3hpsg79rvnz45">
              Straightforward legal advice and tailored representation for businesses, property
              matters and complex disputes.
            </motion.p>

            <motion.div {...enter(0.14)} className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-5 sm:mt-9" data-id="element-126" data-mp="268g3e64amso7">
              <PillButton href="#contact" data-id="element-127" data-mp="2b20tlbznaicu">Book a Consultation</PillButton>
              <a href="#expertise" className="group inline-flex items-center gap-1.5 border-b border-white/40 pb-0.5 text-[14px] font-semibold text-white transition-colors duration-200 hover:border-accent" data-id="element-128" data-mp="2fz0vrbtdj0yo">
                Explore our services
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={2} data-id="element-129" data-mp="2tyirkb537ehj" />
              </a>
            </motion.div>
          </div>

          <motion.div {...enter(0.18)} className="col-span-12 flex flex-col items-stretch gap-5 border-t border-white/[0.14] pt-6 md:flex-row md:items-end md:justify-between xl:col-span-4 xl:flex-col xl:items-end xl:border-t-0 xl:pt-0" data-id="element-130" data-mp="zlsvpnv5rv7i">
            <div className="hidden text-[12px] leading-[1.7] text-white/65 sm:block xl:text-right" data-id="element-131" data-mp="e88vbyqgya2w">
              <ShieldCheckIcon className="mb-1.5 h-4 w-4 text-accent xl:ml-auto" strokeWidth={1.75} aria-hidden="true" data-id="element-132" data-mp="9bs8r8yx43rx" />
              <p data-id="element-133" data-mp="3hqjz0c3nwm6o">Confidential from first contact</p>
              <p data-id="element-134" data-mp="3crpi5fp997p7">Transparent, upfront pricing</p>
              <p data-id="element-135" data-mp="3ct0mbgmm4hgu">15+ years combined experience</p>
            </div>

            <div className="flex w-full items-stretch gap-4 rounded-xl border border-white/[0.14] bg-ink/85 p-3 md:max-w-[380px]" data-id="element-136" data-mp="3rh566nxeiz17">
              <div className="flex flex-1 flex-col justify-between py-1 pl-2" data-id="element-137" data-mp="3mo28q4pi75fq">
                <div data-id="element-138" data-mp="2yy80k0g4q51t">
                  <p className="text-[34px] font-semibold leading-none tracking-[-0.04em] text-white" data-id="element-139" data-mp="2yxofwbz3h2k3">
                    500<span className="text-accent" data-id="element-140" data-mp="2tyve3gt66zzr">+</span>
                  </p>
                  <p className="mt-1.5 text-[12px] text-white/60" data-id="element-141" data-mp="19hcg053nbpa3">Matters advised on</p>
                </div>
                <div className="mt-4 flex -space-x-2" data-id="element-142" data-mp="1ebgo4rone3cr">
                  {avatars.map(a => <span key={a} className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-ink bg-cream text-[10px] font-bold text-ink" data-id="element-143" data-mp="ziejofzzargj">
                      {a}
                    </span>)}
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-ink bg-accent text-[10px] font-bold text-ink" data-id="element-144" data-mp="1x65g8drawjai">
                    +
                  </span>
                </div>
              </div>
              <a href="#about" className="group relative block w-[120px] shrink-0 overflow-hidden rounded-lg sm:w-[150px]" data-id="element-145" data-mp="7ss8eejzcd4n">
                <img src={images.processConsult} alt="Meet the team" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-146" data-mp="bvy8f4gi182o" />
                <span className="absolute inset-0 flex items-center justify-center" data-id="element-147" data-mp="29ktrs1ogcpg5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-ink" data-id="element-148" data-mp="24po5v2v1vas2">
                    <PlayIcon className="ml-0.5 h-3.5 w-3.5 fill-ink" strokeWidth={0} aria-hidden="true" data-id="element-149" data-mp="1zqtng20zvo8h" />
                  </span>
                </span>
                <span className="sr-only" data-id="element-150" data-mp="1qqybhuzm7p7e">Meet the team</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}